/* ============================================================
   <card-swipe> — swipeable product-card image gallery.

   Engine: Swiper.js (assets/swiper-bundle.min.js), loaded ON DEMAND.
   layout/theme.liquid still loads it up front on collection and search
   pages, which open on a grid of cards. Everywhere else this file fetches
   it (URL: theme.settings.swiperJs, snippets/js-variables.liquid) the first
   time a card with two or more photos comes within 200px of the viewport,
   the same moment the gallery has always mounted. Nothing else in the
   theme or its apps reads window.Swiper. Until it arrives the card shows
   its featured image, exactly as it always did before mounting.

   This element is a thin, lazy-mounting host: when the card nears the
   viewport it clones the sibling <template> images into a Swiper, and
   keeps the theme's OWN dots + (desktop) arrows + hover-peek so the look
   is byte-for-byte identical to the previous custom carousel.

   Why Swiper: its touch engine (touchAngle / threshold) reliably releases
   VERTICAL page scrolling to the browser on every engine — Chrome/Android
   and the Instagram / in-app WebViews included — which the old hand-rolled
   gesture code did not (it locked to a horizontal swipe on the first touch
   sample's sideways lead and preventDefault()'d the whole gesture, so it
   scrolled fine on Safari but stuck on Chrome).

   - Touch: Swiper finger-swipe, axis-aware (vertical scroll never hijacked).
     Inside a mobile card-carousel (.slider--tablet) the photo swipe still
     works; swiping on past the last (or first) photo hands the gesture to the
     carousel, which moves to the next (or previous) card. Swiping on the
     card's text still scrolls the carousel natively. (Swiper's own CSS sets
     touch-action: pan-y on the gallery, so turning finger-drag off here left
     a dead zone where neither the photos nor the carousel moved.)
   - Desktop (no touch): hover-to-peek (mouse-x zones, applied once the
     pointer rests) + dots/arrows.
   - Lives inside the card's <a href>: a swipe is suppressed from navigating
     (Swiper preventClicks); a tap still opens the product. Dots/arrows stop
     their own clicks from bubbling to the <a>.

   This file also defines <sunya-video-src> (at the end), which picks a
   smaller MP4 for card and gallery videos. It lives here because this is
   the one card script every page loads.
   ============================================================ */
(function () {
  if (window.customElements && customElements.get('card-swipe')) return;

  var cfg = (window.theme && theme.config) || {};
  var isTouch = cfg.isTouch || ('ontouchstart' in window) || (navigator.maxTouchPoints > 0);
  var reduceMotion = !!cfg.motionReduced;
  var mqMobile = window.matchMedia ? window.matchMedia('(max-width: 767px)') : { matches: false };

  // ── Swiper, on demand ──────────────────────────────────────────
  var SWIPER_SRC = (window.theme && theme.settings && theme.settings.swiperJs) || '';
  var swiperPromise = null;

  function loadSwiper() {
    if (typeof window.Swiper === 'function') return Promise.resolve(window.Swiper);
    if (swiperPromise) return swiperPromise;
    if (!SWIPER_SRC) return Promise.reject(new Error('[card-swipe] theme.settings.swiperJs is not set'));
    swiperPromise = new Promise(function (resolve, reject) {
      var s = document.createElement('script');
      s.src = SWIPER_SRC;
      s.async = true;
      s.onload = function () {
        if (typeof window.Swiper === 'function') { resolve(window.Swiper); return; }
        swiperPromise = null;
        reject(new Error('[card-swipe] Swiper missing after load'));
      };
      s.onerror = function () {
        swiperPromise = null;            // the next card to come into view retries
        if (s.parentNode) s.parentNode.removeChild(s);
        reject(new Error('[card-swipe] Swiper failed to load'));
      };
      document.head.appendChild(s);
    });
    return swiperPromise;
  }

  // Shared, so any future consumer reuses the one request.
  window.theme = window.theme || {};
  if (!theme.loadSwiper) theme.loadSwiper = loadSwiper;

  // ── Late stylesheets ───────────────────────────────────────────
  // card-swipe.css and Swiper's own CSS load without blocking the page
  // (layout/theme.liquid: media="print" and fetchpriority=low, switched to
  // "all" by their onload). A gallery mounted before they applied was laid
  // out in normal flow: its dots sat half-way down the photo and jumped to
  // the bottom edge when the sheet arrived, a layout shift on every phone
  // load. Mounting now waits for both sheets. Until then the card shows its
  // featured photo, as it does before any mount; a sheet that fails to load
  // leaves it that way, rather than mounting an unstyled gallery.
  var LATE_CSS = ['card-swipe.css', 'swiper-bundle.min.css'];
  var cssState = 0;              // 0 not checked, 1 waiting, 2 applied, 3 failed
  var cssQueue = [];

  function whenStyled(cb) {
    if (cssState === 2) { cb(); return; }
    if (cssState === 3) return;
    cssQueue.push(cb);
    if (cssState === 1) return;
    cssState = 1;

    var pending = 0;
    var finish = function (ok) {
      if (cssState !== 1) return;
      if (!ok) { cssState = 3; cssQueue = []; return; }
      if (--pending > 0) return;
      cssState = 2;
      var queue = cssQueue;
      cssQueue = [];
      queue.forEach(function (fn) { fn(); });
    };

    LATE_CSS.forEach(function (name) {
      var link = document.querySelector('link[rel="stylesheet"][href*="' + name + '"]');
      // No such link (another layout), or its onload already switched it on.
      if (!link || !link.media || link.media === 'all') return;
      pending++;
      // The inline onload was registered first, so it has already run when
      // this listener does; setting media here as well covers a page where
      // that inline handler could not run.
      link.addEventListener('load', function () {
        if (link.media !== 'all') link.media = 'all';
        finish(true);
      }, { once: true });
      link.addEventListener('error', function () { finish(false); }, { once: true });
    });

    pending++;
    finish(true);                // releases the queue now if nothing is pending
  }

  // Cards no longer ship their gallery as a <template> of five full <img>
  // tags (~4.5 KB of srcset URLs per card, a third of the home page's HTML).
  // snippets/product-card.liquid prints one compact list instead,
  //   data-media='[[url, width, height, alt], ...]'
  // and this rebuilds the very same markup image_tag produced, the first time
  // the card is mounted. The widths and the "only widths up to the photo's own
  // width" rule mirror image_tag's widths: '180,360,540,720,900,1080'.
  var SWIPE_WIDTHS = [180, 360, 540, 720, 900, 1080];

  function escapeAttr(value) {
    return String(value == null ? '' : value).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');
  }

  function buildTemplate(host) {
    var list;
    try { list = JSON.parse(host.getAttribute('data-media') || '[]'); } catch (e) { return null; }
    if (!Array.isArray(list) || !list.length) return null;
    var sizes = host.getAttribute('data-sizes') || 'auto';
    var cls = 'media media--height' + (host.hasAttribute('data-contain') ? ' media--contain' : '') + ' w-full h-full overflow-hidden';
    var html = list.map(function (m) {
      var url = m[0], w = +m[1], h = +m[2], alt = m[3];
      var srcset = SWIPE_WIDTHS.filter(function (x) { return x <= w; }).map(function (x) {
        return url.replace(/([?&])width=\d+/, '$1width=' + x) + ' ' + x + 'w';
      }).join(', ');
      return '<div class="' + cls + '"><img src="' + escapeAttr(url) + '" alt="' + escapeAttr(alt) + '" srcset="' + escapeAttr(srcset) +
        '" width="' + w + '" height="' + h + '" loading="lazy" sizes="' + escapeAttr(sizes) + '" decoding="async" is="lazy-image"></div>';
    }).join('');
    var tpl = document.createElement('template');
    tpl.innerHTML = html;
    return tpl;
  }

  class CardSwipe extends HTMLElement {
    constructor() {
      super();
      this._mounted = false;
      this._index = 0;
    }

    connectedCallback() {
      if (this._observing || this._mounted) return;
      this._observing = true;

      var mount = this.requestMount.bind(this);
      if (window.Motion && Motion.inView) {
        this._stopInView = Motion.inView(this, function () { mount(); }, { margin: '200px 0px 200px 0px' });
      } else if ('IntersectionObserver' in window) {
        this._io = new IntersectionObserver(function (entries, obs) {
          for (var i = 0; i < entries.length; i++) {
            if (entries[i].isIntersecting) { obs.disconnect(); mount(); break; }
          }
        }, { rootMargin: '200px 0px 200px 0px' });
        this._io.observe(this);
      } else {
        mount();
      }
    }

    disconnectedCallback() {
      this.cancelPeek();
      if (this._io) this._io.disconnect();
      if (this._stopInView) this._stopInView();
      if (this.swiper && this.swiper.destroy) {
        try { this.swiper.destroy(true, true); } catch (e) {}
      }
    }

    // Fetch Swiper if it is not here yet, wait for the gallery's stylesheets
    // (whenStyled, above), then mount. Idempotent.
    requestMount() {
      if (this._mounted || this._swiperQueued) return;
      var tpl = this.template;
      // One photo (or none): nothing to swipe, so never fetch the engine for it.
      if (!tpl || tpl.content.querySelectorAll('.media').length < 2) return;
      this._swiperQueued = true;
      var self = this;
      var go = function () {
        self._swiperQueued = false;
        if (self.isConnected) self.mount();
      };
      if (typeof window.Swiper === 'function') { whenStyled(go); return; }
      loadSwiper().then(function () {
        whenStyled(go);
      }, function (err) {
        self._swiperQueued = false;      // featured image stays; nothing breaks
        if (window.console) console.warn(err && err.message);
      });
    }

    get template() {
      var prev = this.previousElementSibling;
      if (prev && prev.tagName === 'TEMPLATE') return prev;
      var inner = this.querySelector('template');
      if (inner) return inner;
      if (!this._builtTemplate && this.hasAttribute('data-media')) this._builtTemplate = buildTemplate(this);
      return this._builtTemplate || null;
    }

    mount() {
      if (this._mounted) return;
      var tpl = this.template;
      if (!tpl) return;

      var frag = tpl.content.cloneNode(true);
      var media = frag.querySelectorAll('.media');
      this.count = media.length;
      if (this.count < 2) return;                 // nothing to swipe — keep the featured image
      if (typeof Swiper === 'undefined') return;  // engine missing — degrade to the featured image

      this._mounted = true;

      // Build the Swiper DOM: .swiper > .swiper-wrapper > .swiper-slide(.media)
      var container = document.createElement('div');
      container.className = 'swiper card-swipe__swiper';
      var wrapper = document.createElement('div');
      wrapper.className = 'swiper-wrapper';
      this.slides = [];
      for (var i = 0; i < media.length; i++) {
        var slide = document.createElement('div');
        slide.className = 'swiper-slide';
        slide.appendChild(media[i]);
        wrapper.appendChild(slide);
        this.slides.push(slide);
      }
      container.appendChild(wrapper);
      this.appendChild(container);
      this.swiperEl = container;

      this.setAttribute('role', 'group');

      this.buildDots();
      this.buildArrows();

      // Finger-drag wherever the device has touch. Inside a mobile card-carousel
      // the swipe also hands off to the carousel at the gallery's edges (below).
      this._swipeEnabled = isTouch;
      this._carousel = this.closest('.slider--tablet');

      var self = this;
      this.swiper = new Swiper(container, {
        slidesPerView: 1,
        spaceBetween: 0,
        speed: reduceMotion ? 0 : 350,
        threshold: 5,            // ignore sub-5px jitter before a swipe starts
        touchAngle: 45,          // >45° from horizontal => release to vertical page scroll
        resistanceRatio: 0.85,
        followFinger: true,
        allowTouchMove: this._swipeEnabled,  // finger-drag only where we own the gesture
        simulateTouch: false,                // desktop uses hover-peek + arrows, not mouse-drag
        watchOverflow: true,
        a11y: false,             // we keep our own aria on the dots/arrows
        on: {
          slideChange: function () { self._index = this.activeIndex; self.updateUI(); },
          touchStart: function () { self._startIndex = this.activeIndex; },
          touchEnd: function () { self.handOffAtEdge(this); }
        }
      });

      // Desktop hover-to-peek (mouse-x zones). Programmatic slideTo ignores
      // allowTouchMove, so this works while finger-drag stays off on desktop.
      if (!isTouch) this.bindHover();
      this.addEventListener('keydown', this.onKeydown.bind(this));

      // Sunya navigation (modern mode): the card's featured photo is already
      // on screen under this gallery. Keep the gallery invisible, and that
      // photo showing, until slide 1 (the same photo) can paint, so mounting
      // never blanks a card. Legacy mode: unchanged.
      var first = this.slides[0] && this.slides[0].querySelector('img');
      if (window.sunyaNav && window.sunyaNav.modern && first && !(first.complete && first.naturalWidth)) {
        var self = this;
        var done = function () {
          self.classList.remove('is-pending');
          self.removeEventListener('pointerenter', done);
          self.removeEventListener('pointerdown', done);
        };
        this.classList.add('is-pending');
        first.addEventListener('load', function () {
          if (first.decode) first.decode().then(done, done); else done();
        }, { once: true });
        first.addEventListener('error', done, { once: true });
        // A shopper reaching for the gallery (hover-peek, swipe) sees it at once.
        this.addEventListener('pointerenter', done);
        this.addEventListener('pointerdown', done);
      }

      this.classList.add('is-ready');
      this.updateUI();
    }

    // A swipe that starts on the last photo and keeps going left (or on the first
    // photo going right) moves the surrounding card-carousel by one card.
    handOffAtEdge(swiper) {
      var carousel = this._carousel;
      if (!carousel || !mqMobile.matches || !swiper || !swiper.touches) return;
      var diff = swiper.touches.diff || 0;
      var start = this._startIndex;
      var last = this.count - 1;
      if (Math.abs(diff) < 40) return;
      if (diff < 0 && start === last && typeof carousel.next === 'function') carousel.next();
      else if (diff > 0 && start === 0 && typeof carousel.previous === 'function') carousel.previous();
    }

    buildDots() {
      var self = this;
      var nav = document.createElement('div');
      nav.className = 'card-swipe__dots';
      this.dots = [];
      for (var i = 0; i < this.count; i++) {
        var dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'card-swipe__dot';
        dot.setAttribute('aria-label', 'Go to image ' + (i + 1));
        (function (idx) {
          dot.addEventListener('click', function (e) {
            e.preventDefault();
            e.stopPropagation();   // don't let the dot click bubble to the card <a>
            self.cancelPeek();
            if (self.swiper) self.swiper.slideTo(idx);
          });
        })(i);
        nav.appendChild(dot);
        this.dots.push(dot);
      }
      this.appendChild(nav);
    }

    buildArrows() {
      this.prevBtn = this._makeArrow('prev', 'Previous image', 'M15 18l-6-6 6-6');
      this.nextBtn = this._makeArrow('next', 'Next image', 'M9 6l6 6-6 6');
    }

    _makeArrow(dir, label, path) {
      var self = this;
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'card-swipe__arrow card-swipe__arrow--' + dir;
      btn.setAttribute('aria-label', label);
      btn.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" ' +
        'stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="' + path + '"/></svg>';
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        // A peek still waiting must not undo the step a moment later.
        self.cancelPeek();
        if (!self.swiper || btn.getAttribute('aria-disabled') === 'true') return;
        if (dir === 'next') self.swiper.slideNext();
        else self.swiper.slidePrev();
      });
      this.appendChild(btn);
      return btn;
    }

    bindHover() {
      var self = this;
      // The strips under the arrows are left out of the peek: reaching for
      // the right arrow used to peek to the last photo first, which disabled
      // the arrow, and the click then fell through and opened the product.
      var EDGE = 40;
      // Hover intent. Any path from the photo to an arrow crosses the
      // outermost zone, and peeking on the way made the arrow step from the
      // last (or first) photo, or sit disabled. A zone now applies only once
      // the pointer rests in it this long, and reaching a strip cancels it.
      var REST = 110;
      var lastX = 0;
      var apply = function () {
        self._peekTimer = null;
        if (!self.swiper) return;
        var w = self.clientWidth;
        if (!w) return;
        var x = lastX - self.getBoundingClientRect().left;
        var edge = w > EDGE * 4 ? EDGE : 0;
        if (x < edge || x > w - edge) return;
        var zone = Math.floor((x - edge) / ((w - 2 * edge) / self.count));
        if (zone < 0) zone = 0;
        if (zone > self.count - 1) zone = self.count - 1;
        self.swiper.slideTo(zone);   // animated peek (matches the previous behaviour)
      };
      this.addEventListener('mousemove', function (e) {
        self.cancelPeek();
        if (!self.swiper) return;
        var w = self.clientWidth;
        var x = e.clientX - self.getBoundingClientRect().left;
        var edge = w > EDGE * 4 ? EDGE : 0;
        if (!w || x < edge || x > w - edge) return;
        lastX = e.clientX;
        self._peekTimer = setTimeout(apply, REST);
      });
      this.addEventListener('mouseleave', function () {
        self.cancelPeek();
        if (self.swiper) self.swiper.slideTo(0);
      });
    }

    cancelPeek() {
      if (this._peekTimer) clearTimeout(this._peekTimer);
      this._peekTimer = null;
    }

    onKeydown(e) {
      if (!this.swiper) return;
      if (e.key === 'ArrowRight') { e.preventDefault(); this.swiper.slideNext(); }
      else if (e.key === 'ArrowLeft') { e.preventDefault(); this.swiper.slidePrev(); }
    }

    updateUI() {
      var idx = this.swiper ? this.swiper.activeIndex : this._index;
      if (this.dots) {
        this.dots.forEach(function (d, i) {
          var on = i === idx;
          d.classList.toggle('is-active', on);
          if (on) d.setAttribute('aria-current', 'true');
          else d.removeAttribute('aria-current');
        });
      }
      // aria-disabled, not disabled: a disabled button inside the card's link
      // lets the click through, so a tap on an end arrow opened the product.
      if (this.prevBtn) this.prevBtn.setAttribute('aria-disabled', String(this.swiper ? this.swiper.isBeginning : idx <= 0));
      if (this.nextBtn) this.nextBtn.setAttribute('aria-disabled', String(this.swiper ? this.swiper.isEnd : idx >= this.count - 1));
    }
  }

  customElements.define('card-swipe', CardSwipe);
})();

/* ============================================================
   <sunya-video-src> — a smaller MP4 for browsers without HLS.

   Product videos (snippets/media.liquid in the product gallery,
   snippets/product-video.liquid on cards) list HLS first, then the largest
   MP4 that Liquid's media.sources offers. On this store that list holds only
   the m3u8 and the 1080p (7.2 Mbps) MP4, although Shopify also encodes 480p
   and 720p MP4s, which the product's JSON (/products/<handle>.js) lists.
   A browser that plays HLS (Safari, Chrome on Android) streams adaptively
   and needs nothing from here. One that cannot (Firefox, some in-app
   browsers) downloaded the 1080p file: 12 MB into a 390 px gallery frame,
   up to 3.6 MB into each 175 px card.

   The element sits inside the <video> of the <template> that video-media
   (assets/theme.js) clones the first time it plays. Elements in a template
   are inert, so this one starts when the clone is inserted: synchronously,
   before video-media calls play() and before the browser picks a source.
   It then puts the right MP4 first:
   - card: the 480p file on a phone, the 720p file on larger screens;
   - gallery: on a phone, the 720p file; larger screens keep the 1080p one.
   With Data Saver on, or on a 2G/3G connection, the smallest file.

   Until the product's JSON is here (one request per product per page) the
   MP4s stay out of the <video>, so the browser waits on the poster instead of
   starting the 1080p download. For videos in the page's own HTML,
   snippets/sunya-video-hold.liquid already took them out while the page was
   parsing (listed in data-held), because theme.js can clone a video before
   this script has run. Such a clone waits with no MP4 until this element is
   defined and upgraded inside it, then carries on the same way. For a video
   on screen at load, that snippet also asked for the JSON while the page
   parsed (window.SunyaVideoJson), so it is usually here by the time the
   video is cloned.
   Whatever goes wrong (no JSON within 5 s, a failed request, no smaller
   file), the original MP4s go back and the video plays as written. A video
   whose file the browser had already chosen is left alone.
   ============================================================ */
(function () {
  if (!window.customElements || customElements.get('sunya-video-src')) return;

  var HOLD_MS = 5000;
  var products = {};             // product URL -> { promise, data }

  function productData(url) {
    var entry = products[url];
    if (!entry) {
      entry = products[url] = { data: null };
      // snippets/sunya-video-hold.liquid asks for it while the page parses
      // when the video is on screen at load; reuse that request.
      var early = window.SunyaVideoJson && window.SunyaVideoJson[url];
      entry.promise = (early || fetch(url.split(/[?#]/)[0] + '.js', { credentials: 'same-origin' })
        .then(function (r) {
          if (!r.ok) throw new Error('HTTP ' + r.status);
          return r.json();
        }))
        .then(function (data) {
          entry.data = data;
          return data;
        });
      entry.promise.catch(function () {});   // each caller handles failure itself
    }
    return entry;
  }

  function leanConnection() {
    var c = navigator.connection;
    return !!(c && (c.saveData || /2g|3g/.test(c.effectiveType || '')));
  }

  function fileName(url) {
    return String(url || '').split(/[?#]/)[0].split('/').pop();
  }

  function sourceEl(src, type) {
    var el = document.createElement('source');
    el.setAttribute('src', src);
    el.type = type || 'video/mp4';
    return el;
  }

  // The MP4 to put first, or null to leave the video as written.
  function choose(data, mediaId, mode, phone) {
    var media = (data && data.media) || [];
    var list = [];
    for (var i = 0; i < media.length; i++) {
      if (String(media[i].id) !== mediaId) continue;
      (media[i].sources || []).forEach(function (s) {
        if (s && s.url && s.format === 'mp4' && s.height > 0 && s.height <= 720) list.push(s);
      });
      break;
    }
    if (!list.length) return null;
    list.sort(function (a, b) { return a.height - b.height; });
    var smallest = (mode === 'card' && phone) || leanConnection();
    return smallest ? list[0] : list[list.length - 1];
  }

  class SunyaVideoSrc extends HTMLElement {
    connectedCallback() {
      if (this._seen) return;
      this._seen = true;
      try {
        this.start();
      } catch (e) {
        this.restore();
      }
    }

    start() {
      var video = this.parentNode;
      if (!video || video.localName !== 'video') return;
      // The browser has already chosen a file: too late to change it.
      if (video.currentSrc || video.networkState === 1 || video.networkState === 2) return;
      this.video = video;

      var hls = null;
      var mp4s = [];
      for (var el = video.firstElementChild; el; el = el.nextElementSibling) {
        if (el.localName !== 'source') continue;
        if (/mpegurl/i.test(el.type)) hls = hls || el;
        else if (/mp4/i.test(el.type)) mp4s.push(el);
      }
      if (!mp4s.length) {
        mp4s = this.heldSources();
        if (!mp4s.length) return;
        this.held = true;          // already out of the <video>
      }
      this.mp4s = mp4s;

      var mode = this.getAttribute('data-mode') === 'gallery' ? 'gallery' : 'card';
      var phone = !!(window.matchMedia && matchMedia('(max-width: 767px)').matches);
      var url = this.getAttribute('data-src');
      var id = this.getAttribute('data-media');
      // HLS comes first in the list and adapts by itself; larger screens keep
      // the gallery's 1080p file. Either way the video plays as written.
      if ((hls && video.canPlayType(hls.type)) || (mode === 'gallery' && !phone) || !url || !id) {
        this.restore();
        return;
      }

      var entry = productData(url);
      if (entry.data) {
        // Known already (an earlier video of the same product).
        var known = this.sourceFor(choose(entry.data, id, mode, phone));
        if (this.held) this.restore(known);
        else if (known) video.insertBefore(known, mp4s[0]);   // not chosen yet: straight in front
        return;
      }

      // Hold the MP4s back until the JSON answers. With nothing it can play,
      // the <video> keeps its poster and waits for a source to be added.
      if (!this.held) {
        this.held = true;
        for (var i = 0; i < mp4s.length; i++) video.removeChild(mp4s[i]);
      }
      var self = this;
      this.timer = setTimeout(function () { self.restore(); }, HOLD_MS);
      entry.promise.then(function (data) {
        var pick = null;
        try { pick = self.sourceFor(choose(data, id, mode, phone)); } catch (e) {}
        self.restore(pick);
      }, function () {
        self.restore();
      });
    }

    // The MP4s that snippets/sunya-video-hold.liquid took out, as <source>s.
    heldSources() {
      var list = [];
      try { list = JSON.parse(this.getAttribute('data-held') || '[]'); } catch (e) {}
      if (!Array.isArray(list)) return [];
      return list.filter(function (x) { return x && x[0]; }).map(function (x) { return sourceEl(x[0], x[1]); });
    }

    // A <source> for the chosen rendition, or null when there is none or it
    // is a file the <video> already lists.
    sourceFor(pick) {
      if (!pick) return null;
      var name = fileName(pick.url);
      for (var i = 0; i < this.mp4s.length; i++) {
        if (fileName(this.mp4s[i].getAttribute('src')) === name) return null;
      }
      return sourceEl(pick.url, pick.mime_type);
    }

    // Ends a hold: the chosen file (if any), then the original MP4s. They are
    // appended at the end, which is where a <video> waiting for a source looks
    // for new ones; if the browser has not started choosing yet, it reads them
    // in this order all the same.
    restore(first) {
      if (!this.held || !this.video) return;
      this.held = false;
      clearTimeout(this.timer);
      this.removeAttribute('data-held');
      var mp4s = this.mp4s || this.heldSources();
      if (first) this.video.appendChild(first);
      for (var i = 0; i < mp4s.length; i++) this.video.appendChild(mp4s[i]);
    }
  }

  // Take over the early requests now, so a JSON that has already arrived is
  // known (entry.data) by the time its video is cloned.
  if (window.SunyaVideoJson) Object.keys(window.SunyaVideoJson).forEach(productData);

  customElements.define('sunya-video-src', SunyaVideoSrc);
})();
